import { Link } from 'react-router';
import { ThemeToggle } from '@mieweb/ui';
import { BrandLockup } from '../components/BrandMark';

export function TermsPage() {
  const lastUpdated = 'February 4, 2026';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link to="/login" className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Terms of Service</h1>
          <p className="text-slate-500 dark:text-neutral-400 mb-8">Last updated: {lastUpdated}</p>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-lg p-4 mb-8">
            <p className="text-amber-800 dark:text-amber-300 text-sm">
              <strong>Summary:</strong> These terms govern your use of Pollenate. By using our service, 
              you agree to these terms. We provide a feedback collection platform, and you're responsible 
              for how you use it.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you and 
              BlueHive Health, LLC ("BlueHive Health," "we," "us," or "our") governing your access to 
              and use of the Pollenate feedback platform, including any associated websites, applications, 
              APIs, and services (collectively, the "Service").
            </p>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              By accessing or using the Service, you acknowledge that you have read, understood, and 
              agree to be bound by these Terms. If you do not agree to these Terms, you may not access 
              or use the Service.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              We reserve the right to modify these Terms at any time. We will notify you of material 
              changes by posting the updated Terms on the Service or by sending you an email. Your 
              continued use of the Service after such modifications constitutes your acceptance of 
              the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">2. Description of Service</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              Pollenate is a feedback collection and management platform that enables businesses and 
              developers to:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Embed customizable feedback widgets on websites and applications</li>
              <li>Collect user feedback including ratings, comments, and sentiment data</li>
              <li>Organize and analyze feedback through dashboards and reports</li>
              <li>Integrate feedback data with other systems via APIs</li>
              <li>Collaborate with team members on feedback management</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              The Service is provided on an "as is" and "as available" basis. We may modify, suspend, 
              or discontinue any aspect of the Service at any time without prior notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">3. Account Registration</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              To access certain features of the Service, you must create an account. When creating 
              an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security and confidentiality of your login credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              We reserve the right to suspend or terminate your account if any information provided 
              proves to be inaccurate, outdated, or incomplete, or if we believe you have violated 
              these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">4. Acceptable Use</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              You agree to use the Service only for lawful purposes and in accordance with these Terms. 
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Use the Service in any way that violates applicable laws or regulations</li>
              <li>Collect feedback from users without their knowledge or consent</li>
              <li>Use the Service to collect sensitive personal information without proper safeguards</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers connected to the Service</li>
              <li>Use the Service to transmit malware, spam, or other harmful content</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
              <li>Use the Service to compete with or replicate the Service</li>
              <li>Resell or redistribute the Service without our written permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">5. Your Content and Data</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>Your Content:</strong> You retain ownership of all content, data, and information 
              you submit to the Service ("Your Content"). By submitting Your Content, you grant us a 
              non-exclusive, worldwide, royalty-free license to use, store, and process Your Content 
              solely for the purpose of providing the Service to you.
            </p>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>Feedback Data:</strong> You are responsible for ensuring that you have obtained 
              all necessary consents and authorizations to collect feedback from your users. You agree 
              to comply with all applicable privacy laws and regulations.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              <strong>Data Retention:</strong> We will retain Your Content for as long as your account 
              is active or as needed to provide you with the Service. Upon account termination, we will 
              delete Your Content within 30 days, unless required by law to retain it longer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">6. Intellectual Property</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              The Service, including all content, features, and functionality (excluding Your Content), 
              is owned by BlueHive Health, LLC and is protected by copyright, trademark, and other 
              intellectual property laws.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              Pollenate is proprietary software. All rights reserved. The Service, including all code, 
              features, and infrastructure, is the exclusive property of BlueHive Health, LLC. 
              Unauthorized reproduction, distribution, or use of any portion of the Service is 
              strictly prohibited.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">7. Payment Terms</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              Certain features of the Service require payment. If you choose a paid plan:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>You agree to pay all fees associated with your selected plan</li>
              <li>Fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>We may change our pricing with 30 days' notice</li>
              <li>Failure to pay may result in suspension or termination of your account</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">8. Disclaimers</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              We do not warrant that the Service will be uninterrupted, error-free, or secure, or 
              that any defects will be corrected. We do not warrant the accuracy, completeness, or 
              usefulness of any information provided through the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLUEHIVE HEALTH, LLC SHALL NOT BE LIABLE FOR 
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF 
              PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, 
              GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING 
              TO THESE TERMS OR THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO US 
              IN THE TWELVE MONTHS PRIOR TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">10. Indemnification</h2>
            <p className="text-slate-600 dark:text-neutral-400">
              You agree to indemnify, defend, and hold harmless BlueHive Health, LLC and its officers, 
              directors, employees, agents, and affiliates from and against any claims, liabilities, 
              damages, losses, and expenses (including reasonable attorneys' fees) arising out of or 
              in any way connected with your access to or use of the Service, your violation of these 
              Terms, or your violation of any rights of another party.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">11. Termination</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              You may terminate your account at any time by contacting us or using the account 
              deletion feature in your settings.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              We may terminate or suspend your account and access to the Service immediately, 
              without prior notice or liability, for any reason, including if you breach these Terms. 
              Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">12. Governing Law</h2>
            <p className="text-slate-600 dark:text-neutral-400">
              These Terms shall be governed by and construed in accordance with the laws of the 
              State of Indiana, United States, without regard to its conflict of law provisions. 
              Any disputes arising under these Terms shall be resolved in the state or federal 
              courts located in Fort Wayne, Indiana.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">13. General Provisions</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, 
              constitute the entire agreement between you and BlueHive Health, LLC regarding the Service.
            </p>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>Severability:</strong> If any provision of these Terms is found to be 
              unenforceable, the remaining provisions will continue in full force and effect.
            </p>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms 
              will not be considered a waiver of such right or provision.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              <strong>Assignment:</strong> You may not assign or transfer these Terms without our 
              prior written consent. We may assign these Terms without restriction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">14. Contact Information</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6">
              <p className="text-slate-700 dark:text-neutral-300 font-medium">BlueHive Health, LLC</p>
              <p className="text-slate-600 dark:text-neutral-400">Email: legal@pollenate.dev</p>
              <p className="text-slate-600 dark:text-neutral-400">Website: <a href="https://pollenate.dev" className="text-primary hover:underline">pollenate.dev</a></p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-neutral-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500 dark:text-neutral-400">
            <p>© 2026 <a href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-neutral-300 transition-colors">BlueHive Health</a>, LLC. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="hover:text-slate-700 dark:text-neutral-300 transition-colors">Privacy Policy</Link>
              <Link to="/" className="hover:text-slate-700 dark:text-neutral-300 transition-colors">Back to Home</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
